import type { PropertyName } from "@frui.ts/helpers";
import { bound } from "@frui.ts/helpers";
import type { ScreenBase } from "@frui.ts/screens";
import { Router, watchBusy } from "@frui.ts/screens";
import { Modules } from "config";
import RejectedCustomerRefundCandidateResponse from "entities/rejectedCustomerRefundCandidateResponse";
import RejectedCustomerRefundCandidateSearchRequest from "entities/rejectedCustomerRefundCandidateSearchRequest";
import RejectedCustomerRefundCandidateStatus from "entities/rejectedCustomerRefundCandidateStatus";
import { computed } from "mobx";
import RejectedCustomerRefundCandidatesRepository from "repositories/rejectedCustomerRefundCandidatesRepository";
import ConfirmationService from "services/confirmationService";
import EnumService from "services/enumService";
import EventBus, { customerRefundCandidateRejected, rejectedCustomerRefundCandidateRecreated } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import OptionsService from "services/optionsService";
import SecurityService from "services/securityService";
import type CountrySelectViewModel from "viewModels/countrySelectViewModel";
import ListViewModel from "viewModels/listViewModel";
import type PagingManagerViewModel from "viewModels/pagingManagerViewModel";
import RejectedCustomerRefundCandidateDetailViewModel from "./rejectedCustomerRefundCandidateDetailViewModel";

const navigationName = "rejected-customer-refund-candidates";

@Router.registerRoute({ route: navigationName, name: Router.Self, children: [RejectedCustomerRefundCandidateDetailViewModel] })
export default class RejectedCustomerRefundCandidatesViewModel extends ListViewModel<
  RejectedCustomerRefundCandidateResponse,
  RejectedCustomerRefundCandidateDetailViewModel,
  ScreenBase,
  RejectedCustomerRefundCandidateSearchRequest
> {
  readonly modelName = "rejectedCustomerRefundCandidate";
  countrySelectViewModel: CountrySelectViewModel;

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get allowToRecreate() {
    return this.securityService.allowTo("finance", "write") || this.securityService.allowTo("callcenter.finance", "write");
  }

  @computed
  get statusOptions() {
    return this.optionsService.internalEnumOptions(RejectedCustomerRefundCandidateStatus);
  }

  constructor(
    protected localizationService: LocalizationService,
    protected securityService: SecurityService,
    protected eventBus: EventBus,
    protected repository: RejectedCustomerRefundCandidatesRepository,
    protected detailViewModelFactory: ReturnType<typeof RejectedCustomerRefundCandidateDetailViewModel.Factory>,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    private enumService: EnumService,
    private optionsService: OptionsService,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    private pagingManagerFactory: ReturnType<typeof PagingManagerViewModel.Factory>
  ) {
    super();
    this.navigationName = navigationName;
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  onInitialize(): Promise<any> | void {
    this.unsubscriptions.push(this.eventBus.subscribe(customerRefundCandidateRejected, this.loadData));
    return super.onInitialize();
  }

  @bound
  allowItemDetail(item: RejectedCustomerRefundCandidateResponse) {
    return false;
  }

  @bound
  @watchBusy
  async onRecreateClick(item: RejectedCustomerRefundCandidateResponse) {
    if (!item.permittedActions.recreation.isAllowed) {
      return;
    }

    try {
      const confirmed = await this.confirmationService.showConfirmation(this.translate("recreationConfirmation.message"), {
        title: this.translate("recreationConfirmation.title"),
        confirmButton: { variant: "primary", text: this.translate("recreationConfirmation.confirm") },
      });

      if (confirmed) {
        await this.repository.recreations(item.id);
        this.eventBus.publish(rejectedCustomerRefundCandidateRecreated({ rejectedCustomerRefundCandidate: item }));
        this.notificationService.add({ title: this.translate("candidateRecreated"), type: NotificationType.SUCCESS });
      }
    } catch (e) {
      await this.loadData();
    }
  }

  protected processData(items: RejectedCustomerRefundCandidateResponse[]) {
    return items.sort((a, b) => {
      if (!a.rejectionDate || !b.rejectionDate) {
        return -1;
      }
      return b.rejectionDate.getTime() - a.rejectionDate.getTime();
    });
  }

  protected detailParams(itemId: any): any[] {
    const pagingManager = this.pagingManagerFactory(this, itemId);
    return [pagingManager];
  }

  protected createFilter(): RejectedCustomerRefundCandidateSearchRequest {
    return new RejectedCustomerRefundCandidateSearchRequest();
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `rejectedCustomerRefundCandidates.${code}`, size);
  }

  translateFilterAttribute(attribute: PropertyName<RejectedCustomerRefundCandidateSearchRequest>, size?: number) {
    return this.localizationService.translateAttribute("rejectedCustomerRefundCandidateSearchRequest", attribute, size);
  }
}
